import { useEffect, useState } from "react";
import { useMotionValueEvent, type MotionValue } from "framer-motion";
import { Focus, Minus, Plus } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

export interface FocusTarget {
  /** Matches a `data-canvas-key` inside the canvas world. */
  key: string;
  label: string;
  /** For targets that aren't canvas objects (Next Steps): act instead of moving the view. */
  run?: () => void;
}

const glass: React.CSSProperties = {
  background: "rgba(255,255,255,0.42)",
  backdropFilter: "blur(22px) saturate(180%)",
  WebkitBackdropFilter: "blur(22px) saturate(180%)",
  border: "1px solid rgba(255,255,255,0.6)",
  boxShadow: "0 10px 34px rgba(28,25,23,0.20), inset 0 1px 0 rgba(255,255,255,0.75)",
};

const iconBtn =
  "w-9 h-9 flex items-center justify-center rounded-full text-stone-600 hover:text-stone-900 hover:bg-white/70 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400";

/**
 * Focus / Find Content — one button with a small state machine:
 * - after any manual pan/zoom (and initially), the next press fits the whole journal;
 * - each further press steps to the next target (Shift-click steps back), wrapping;
 * - targets that aren't currently on the canvas are skipped.
 * Plus minimal zoom −/%/+ beside it.
 */
export function CanvasControls({
  scale, resetSignal, onNavigated, getTargets, onFit, onFocusKey, onZoomIn, onZoomOut, onZoomReset,
}: {
  scale: MotionValue<number>;
  /** Changes whenever the user navigates by hand. */
  resetSignal: number;
  /** Called after the control moves the view, so the next manual move resets the cycle. */
  onNavigated: () => void;
  getTargets: () => FocusTarget[];
  onFit: () => void;
  /** Returns false when the target isn't on the canvas, so it can be skipped. */
  onFocusKey: (key: string) => boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomReset: () => void;
}) {
  // null → next press fits; -1 → fitted; n → showing target n.
  const [step, setStep] = useState<number | null>(null);
  useEffect(() => { setStep(null); }, [resetSignal]);

  const [pct, setPct] = useState(() => Math.round(scale.get() * 100));
  useMotionValueEvent(scale, "change", (s) => setPct(Math.round(s * 100)));

  const targets = getTargets();
  const n = targets.length;
  const nextIndex = (from: number, back: boolean) =>
    back ? (from <= 0 ? n - 1 : from - 1) : (from + 1) % n;

  const press = (back: boolean) => {
    onNavigated();
    if (step === null || n === 0) {
      onFit();
      setStep(-1);
      return;
    }
    let i = step;
    for (let tries = 0; tries < n; tries++) {
      i = nextIndex(i, back);
      const t = targets[i];
      if (t.run) { t.run(); setStep(i); return; }
      if (onFocusKey(t.key)) { setStep(i); return; }
    }
    onFit();
    setStep(-1);
  };

  const label =
    step === null || n === 0
      ? "Fit journal"
      : `Next: ${targets[nextIndex(step, false)]?.label ?? "Fit journal"}`;

  return (
    <div
      data-export-hide
      className="absolute left-5 bottom-5 z-30 flex items-center gap-0.5 rounded-full p-1 font-sans"
      style={glass}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={iconBtn}
            aria-label={label}
            onClick={(e) => press(e.shiftKey)}
          >
            <Focus className="w-[18px] h-[18px]" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-[11px]">
          {label}
          {step !== null && n > 0 && <span className="opacity-70"> · Shift-click to go back</span>}
        </TooltipContent>
      </Tooltip>
      <span className="w-px h-5 bg-stone-300/70 mx-0.5" aria-hidden />
      <button type="button" className={iconBtn} aria-label="Zoom out" title="Zoom out" onClick={onZoomOut}>
        <Minus className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={onZoomReset}
        title="Reset zoom to 100%"
        aria-label={`Zoom ${pct}%, reset to 100%`}
        className="min-w-[44px] h-9 px-1 rounded-full text-[11px] tabular-nums text-stone-600 hover:text-stone-900 hover:bg-white/70 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
      >
        {pct}%
      </button>
      <button type="button" className={iconBtn} aria-label="Zoom in" title="Zoom in" onClick={onZoomIn}>
        <Plus className="w-4 h-4" />
      </button>
    </div>
  );
}
